import { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { setModal } from 'slices/modalSlice'
import {
  ID_PERFIL_ADMIN_EMPRESA,
  ID_PERFIL_COMPRADOR,
  ID_PERFIL_VENDEDOR
} from '../../constants/perfiles'
import { GenericModal } from '../reutilizables/modal/GenericModal'
import { Paginacion } from '../reutilizables/paginacion/Paginacion'
import * as fetchLocalidadesAPI from '../servicios/fetchLocalidadesAPI'
import * as fetchProvinciasAPI from '../servicios/fetchProvinciasAPI'
import * as usuariosEBPService from '../servicios/usuariosEBPService'
import * as usuarioService from '../servicios/usuarioService'
import styles from './AdministracionUsuarios.module.css'
import UsuariosFiltros from './filtros/UsuariosFiltros'
import ItemUsuario from './ItemUsuario.jsx'

const ITEMS_POR_PAGINA_DEFAULT = 15
const LONGITUD_DNI = 8
const LONGITUD_CUIT = 11
const LONGITUD_TELEFONO = 10
const LONGITUD_EMAIL = 40
const LONGITUD_NOMBRE_APELLIDO = 50
const INDICE_CAMPOS_COMUNES_ALTA = 5
const ESTADO_USUARIO_HABILITADO = 2
const ESTADO_USUARIO_DESHABILITADO = 3
const ROLES_DISPONIBLES = [
  { value: 'Vendedor', label: 'Vendedor', idPerfil: ID_PERFIL_VENDEDOR },
  { value: 'Comprador', label: 'Comprador', idPerfil: ID_PERFIL_COMPRADOR },
  { value: 'Administrador', label: 'Administrador', idPerfil: ID_PERFIL_ADMIN_EMPRESA }
]

const filtrosConfig = [
  { key: 'usuario', label: 'Colaborador', type: 'text', placeholder: 'Filtrar por colaborador' },
  {
    key: 'email',
    label: 'Email',
    type: 'text',
    placeholder: 'Filtrar por email'
  },
  {
    key: 'estado',
    label: 'Estado',
    type: 'select',
    placeholder: 'Filtrar por estado',
    options: [
      { value: 'todos', label: 'Todos' },
      { value: 'habilitado', label: 'Habilitado' },
      { value: 'inactivo', label: 'Inactivo' },
      { value: 'deshabilitado', label: 'Deshabilitado' }
    ]
  }
]

const buildCamposModalBase = (provincias, localidades, roles, localidadHabilitada) => [
  {
    name: 'nombre',
    label: 'Nombre',
    type: 'text',
    placeholder: 'Ingresá el nombre',
    isFullWidth: false,
    isEditable: true,
    maxLength: LONGITUD_NOMBRE_APELLIDO
  },
  {
    name: 'apellido',
    label: 'Apellido',
    type: 'text',
    placeholder: 'Ingresá el apellido',
    isFullWidth: false,
    isEditable: true,
    maxLength: LONGITUD_NOMBRE_APELLIDO
  },
  {
    name: 'email',
    label: 'Email',
    type: 'email',
    placeholder: 'Ingresá el email',
    isFullWidth: true,
    isEditable: true,
    maxLength: LONGITUD_EMAIL
  },
  {
    name: 'telefono',
    label: 'Teléfono',
    type: 'text',
    placeholder: 'Ingresá el teléfono',
    isFullWidth: false,
    isEditable: true,
    inputMode: 'numeric',
    maxLength: LONGITUD_TELEFONO
  },
  {
    name: 'dni',
    label: 'DNI',
    type: 'text',
    placeholder: 'Ingresá el DNI',
    isFullWidth: false,
    isEditable: true,
    inputMode: 'numeric',
    maxLength: LONGITUD_DNI
  },
  {
    name: 'rol',
    label: 'Rol',
    type: 'select',
    placeholder: 'Seleccioná un rol',
    isFullWidth: false,
    isEditable: true,
    options: roles
  },
  {
    name: 'idProvincia',
    label: 'Provincia',
    type: 'select',
    placeholder: 'Seleccioná la provincia',
    isFullWidth: false,
    isEditable: true,
    options: provincias
  },
  {
    name: 'idLocalidad',
    label: 'Localidad',
    type: 'select',
    placeholder: 'Seleccioná la localidad',
    isFullWidth: false,
    isEditable: localidadHabilitada,
    options: localidades
  }
]

const buildCamposModalAlta = (provincias, localidades, roles, localidadHabilitada) => {
  const camposBase = buildCamposModalBase(provincias, localidades, roles, localidadHabilitada)

  return [
    {
      name: 'usuario',
      label: 'Colaborador',
      type: 'text',
      placeholder: 'Colaborador',
      isFullWidth: false,
      isEditable: false
    },
    ...camposBase.slice(0, INDICE_CAMPOS_COMUNES_ALTA),
    {
      name: 'cuit',
      label: 'CUIT',
      type: 'text',
      placeholder: 'Ingresá el CUIT',
      isFullWidth: false,
      isEditable: true,
      inputMode: 'numeric',
      maxLength: LONGITUD_CUIT
    },
    ...camposBase.slice(INDICE_CAMPOS_COMUNES_ALTA)
  ]
}

const normalizarRespuestaAPI = (data) => (Array.isArray(data) ? data : [])

const aOpcionesSelect = (items, idKey) =>
  items.map((item) => ({ value: item[idKey], label: item.nombre }))

const obtenerDelLocalStorage = (key, defaultValue = '') =>
  String(localStorage.getItem(key) || defaultValue).trim()

const AdministracionUsuarios = () => {
  const dispatch = useDispatch()
  const [usuarios, setUsuarios] = useState([])
  const [provincias, setProvincias] = useState([])
  const [localidades, setLocalidades] = useState([])
  const [cargando, setCargando] = useState(true)
  const [errorCarga, setErrorCarga] = useState('')
  const [razonSocialAdmin, setRazonSocialAdmin] = useState('')
  const [filtros, setFiltros] = useState({
    usuario: '',
    nombre: '',
    apellido: '',
    email: '',
    telefono: '',
    estado: 'todos'
  })
  const [seleccionados, setSeleccionados] = useState([])
  const [mensajeGuardado, setMensajeGuardado] = useState('')
  const [usuarioEditandoId, setUsuarioEditandoId] = useState(null)
  const [paginaActual, setPaginaActual] = useState(1)
  const [itemsPorPagina, setItemsPorPagina] = useState(ITEMS_POR_PAGINA_DEFAULT)
  const [guardando, setGuardando] = useState(false)
  const [isAlta, setIsAlta] = useState(false)
  const {
    values: modalValues,
    modalId: modalActivoId,
    show: modalAbierto
  } = useSelector((state) => state.genericModal)

  const provinciasOptions = useMemo(() => aOpcionesSelect(provincias, 'idProvincia'), [provincias])

  const localidadesOptions = useMemo(
    () => aOpcionesSelect(localidades, 'idLocalidad'),
    [localidades]
  )

  const { camposModal, camposModalAlta } = useMemo(() => {
    const params = [
      provinciasOptions,
      localidadesOptions,
      ROLES_DISPONIBLES,
      Boolean(modalValues?.idProvincia)
    ]
    return {
      camposModal: buildCamposModalBase(...params),
      camposModalAlta: buildCamposModalAlta(...params)
    }
  }, [localidadesOptions, modalValues?.idProvincia, provinciasOptions])

  const obtenerIdProvinciaDesdeLocalidad = useCallback(
    async (idLocalidad) => {
      const idLocalidadNumerico = Number(idLocalidad)

      if (!idLocalidadNumerico || provincias.length === 0) {
        return ''
      }

      for (const provincia of provincias) {
        const { idProvincia } = provincia

        const { data } = await fetchLocalidadesAPI.buscarLocalidadesPorIdProvincia(idProvincia)
        const localidadesResponse = normalizarRespuestaAPI(data)

        if (localidadesResponse.some((l) => l.idLocalidad === idLocalidadNumerico)) {
          setLocalidades(localidadesResponse)
          return String(idProvincia)
        }
      }

      return ''
    },
    [provincias]
  )

  const normalizarTexto = (valor) =>
    String(valor || '')
      .toLowerCase()
      .trim()

  const mapUsuarioApi = (usuarioApi, index) => {
    const idUsuarioEstadoApi = usuarioApi.idUsuarioEstado ?? null
    const idUsuarioEstado =
      idUsuarioEstadoApi === null || idUsuarioEstadoApi === undefined
        ? null
        : Number(idUsuarioEstadoApi)

    return {
      idRegistro: `${usuarioApi.userId || 'sin-id'}-${index}`,
      idUsuario: String(usuarioApi.userId || ''),
      usuario: String(usuarioApi.userName || ''),
      nombre: String(usuarioApi.nombre || ''),
      apellido: String(usuarioApi.apellido || ''),
      email: String(usuarioApi.eMail || ''),
      telefono: String(usuarioApi.telefono || '').trim(),
      dni: String(usuarioApi.dni || ''),
      cuit: String(usuarioApi.cuit || ''),
      idPerfil: usuarioApi.idPerfil || '',
      rol: ROLES_DISPONIBLES.find((r) => r.idPerfil === usuarioApi.idPerfil)?.value || '',
      idLocalidad: usuarioApi.idLocalidad || '',
      idUsuarioEstado,
      habilitadoEnEBM: Boolean(usuarioApi.habilitadoEnEBM),
      fueHabilitado: idUsuarioEstado !== null
    }
  }

  const LIMITE_CAMPO = {
    dni: (v) => v.replace(/\D/g, '').slice(0, LONGITUD_DNI),
    cuit: (v) => v.replace(/\D/g, '').slice(0, LONGITUD_CUIT),
    telefono: (v) => v.replace(/\D/g, '').slice(0, LONGITUD_TELEFONO),
    email: (v) => v.slice(0, LONGITUD_EMAIL),
    nombre: (v) => v.slice(0, LONGITUD_NOMBRE_APELLIDO),
    apellido: (v) => v.slice(0, LONGITUD_NOMBRE_APELLIDO)
  }

  const normalizarValorCampo = (name, value) => LIMITE_CAMPO[name]?.(value) ?? value

  const cargarProvincias = useCallback(async () => {
    try {
      const { data } = await fetchProvinciasAPI.buscarProvinciasEnEBM()
      setProvincias(normalizarRespuestaAPI(data))
    } catch {
      setProvincias([])
    }
  }, [])

  useEffect(() => {
    cargarProvincias()
  }, [cargarProvincias])

  useEffect(() => {
    if (!modalAbierto) {
      return
    }

    if (modalActivoId === 'administracion-usuarios-editar') {
      dispatch(setModal({ campos: camposModal }))
      return
    }

    if (modalActivoId === 'administracion-usuarios-alta') {
      dispatch(setModal({ campos: camposModalAlta }))
    }
  }, [camposModal, camposModalAlta, dispatch, modalAbierto, modalActivoId])

  const cargarUsuarios = useCallback(async () => {
    const userId = obtenerDelLocalStorage('empid')

    if (!userId) {
      setErrorCarga('No se encontró el usuario logueado.')
      setCargando(false)
      return
    }

    try {
      setCargando(true)
      const { data } = await usuariosEBPService.obtenerUsuarios(userId)
      const usuarioLogueado = obtenerDelLocalStorage('usuario')
      setErrorCarga('')
      setUsuarios(
        (Array.isArray(data?.body) ? data.body : [])
          .filter((u) => u.userName !== usuarioLogueado)
          .map(mapUsuarioApi)
      )
    } catch {
      setErrorCarga('Error al cargar los colaboradores. Intente nuevamente.')
    } finally {
      setCargando(false)
    }
  }, [])

  const cargarRazonSocialAdmin = useCallback(async () => {
    const idUsuarioAdmin = obtenerDelLocalStorage('idUsuario')

    if (!idUsuarioAdmin) {
      setRazonSocialAdmin('')
      return
    }

    try {
      const { data } = await usuarioService.buscarPorId(Number(idUsuarioAdmin))
      setRazonSocialAdmin(data?.razonSocial || data?.RazonSocial || '')
    } catch {
      setRazonSocialAdmin('')
    }
  }, [])

  useEffect(() => {
    cargarUsuarios()
    cargarRazonSocialAdmin()
    cargarProvincias()
  }, [cargarProvincias, cargarRazonSocialAdmin, cargarUsuarios])

  const onChangeFiltro = useCallback((campo, valor) => {
    setFiltros((prevState) => ({
      ...prevState,
      [campo]: valor
    }))
  }, [])

  const obtenerEstadoUsuario = (usuario) => {
    if (usuario.habilitadoEnEBM) return 'habilitado'
    if (usuario.fueHabilitado) return 'inactivo'
    return 'deshabilitado'
  }

  const usuariosFiltrados = useMemo(() => {
    const usuariosFiltradosPorTexto = usuarios.filter((u) => {
      const coincideUsuario = normalizarTexto(u.usuario).includes(normalizarTexto(filtros.usuario))
      const coincideNombre = normalizarTexto(u.nombre).includes(normalizarTexto(filtros.nombre))
      const coincideApellido = normalizarTexto(u.apellido).includes(
        normalizarTexto(filtros.apellido)
      )
      const coincideEmail = normalizarTexto(u.email).includes(normalizarTexto(filtros.email))
      const coincideTelefono = normalizarTexto(u.telefono).includes(
        normalizarTexto(filtros.telefono)
      )
      const coincideEstado =
        filtros.estado === 'todos' || obtenerEstadoUsuario(u) === filtros.estado

      return (
        coincideUsuario &&
        coincideNombre &&
        coincideApellido &&
        coincideEmail &&
        coincideTelefono &&
        coincideEstado
      )
    })

    return [...usuariosFiltradosPorTexto].sort((a, b) => {
      const estadoA = obtenerEstadoUsuario(a)
      const estadoB = obtenerEstadoUsuario(b)
      const ordenEstados = { habilitado: 0, inactivo: 1, deshabilitado: 2 }

      if (ordenEstados[estadoA] !== ordenEstados[estadoB]) {
        return ordenEstados[estadoA] - ordenEstados[estadoB]
      }

      return normalizarTexto(a.usuario).localeCompare(normalizarTexto(b.usuario))
    })
  }, [filtros, usuarios])

  useEffect(() => {
    setPaginaActual(1)
  }, [filtros])

  const totalPaginas = Math.max(1, Math.ceil(usuariosFiltrados.length / itemsPorPagina))
  const inicioPagina = (paginaActual - 1) * itemsPorPagina
  const usuariosPaginados = usuariosFiltrados.slice(inicioPagina, inicioPagina + itemsPorPagina)

  useEffect(() => {
    if (paginaActual > totalPaginas) {
      setPaginaActual(totalPaginas)
    }
  }, [paginaActual, totalPaginas])

  const toggleSeleccion = (idRegistro) => {
    setMensajeGuardado('')
    setSeleccionados((prevState) => {
      if (prevState.includes(idRegistro)) {
        return prevState.filter((id) => id !== idRegistro)
      }

      return [...prevState, idRegistro]
    })
  }

  const abrirModal = async (usuario, isAlta) => {
    if (isAlta && usuario.habilitadoEnEBM) {
      return
    }
    if (!isAlta && !usuario.habilitadoEnEBM && !usuario.fueHabilitado) {
      return
    }

    setMensajeGuardado('')
    setIsAlta(isAlta)

    if (!isAlta) {
      setUsuarioEditandoId(usuario.idRegistro)
    }

    const idProvinciaResuelta =
      usuario.idProvincia || (await obtenerIdProvinciaDesdeLocalidad(usuario.idLocalidad))

    const modalConfig = {
      show: true,
      size: 'lg',
      type: 'form',
      title: isAlta
        ? `Dar de alta colaborador ${usuario.usuario}`
        : `Editar colaborador ${usuario.usuario}`,
      modalId: isAlta ? 'administracion-usuarios-alta' : 'administracion-usuarios-editar',
      ...(isAlta && { editMode: true }),
      values: {
        idRegistro: usuario.idRegistro,
        ...(!isAlta && { idUsuario: usuario.idUsuario }),
        ...(isAlta && { usuario: usuario.usuario || '' }),
        nombre: usuario.nombre || '',
        apellido: usuario.apellido || '',
        email: usuario.email || '',
        telefono: usuario.telefono || '',
        dni: usuario.dni || '',
        ...(isAlta && { cuit: usuario.cuit || '' }),
        rol: usuario.rol || '',
        idProvincia: idProvinciaResuelta || '',
        idLocalidad: usuario.idLocalidad || ''
      },
      campos: isAlta
        ? buildCamposModalAlta(
            provinciasOptions,
            localidadesOptions,
            ROLES_DISPONIBLES,
            Boolean(idProvinciaResuelta || usuario.idProvincia)
          )
        : buildCamposModalBase(
            provinciasOptions,
            localidadesOptions,
            ROLES_DISPONIBLES,
            Boolean(idProvinciaResuelta || usuario.idProvincia)
          ),
      errores: {}
    }

    dispatch(setModal(modalConfig))
  }

  const abrirModalEdicion = (usuario) => abrirModal(usuario, false)

  const abrirModalAlta = (usuario) => abrirModal(usuario, true)

  const actualizarEstadoUsuario = (idRegistro) => {
    setUsuarios((prevState) =>
      prevState.map((usuario) => {
        if (usuario.idRegistro !== idRegistro) {
          return usuario
        }

        const nuevoEstado =
          usuario.idUsuarioEstado === ESTADO_USUARIO_HABILITADO
            ? ESTADO_USUARIO_DESHABILITADO
            : ESTADO_USUARIO_HABILITADO
        const esAhoraHabilitado = nuevoEstado === ESTADO_USUARIO_HABILITADO
        return {
          ...usuario,
          idUsuarioEstado: nuevoEstado,
          habilitadoEnEBM: esAhoraHabilitado,
          fueHabilitado: usuario.fueHabilitado || esAhoraHabilitado
        }
      })
    )

    setSeleccionados((prevState) => {
      if (prevState.includes(idRegistro)) {
        return prevState
      }

      return [...prevState, idRegistro]
    })
  }

  const construirUsuarioPayload = (empid, usuario) => {
    const rolSeleccionado =
      usuario.rol ||
      ROLES_DISPONIBLES.find((r) => r.idPerfil === Number(usuario.idPerfil))?.value ||
      ''

    return {
      empId: empid,
      usrId: usuario.usuario,
      dni: usuario.dni || '',
      cuit: usuario.cuit || '',
      email: usuario.email || '',
      nombre: usuario.nombre || '',
      apellido: usuario.apellido || '',
      telefono: usuario.telefono || '',
      idLocalidad: usuario.idLocalidad ? Number(usuario.idLocalidad) : null,
      idPerfil:
        ROLES_DISPONIBLES.find((r) => r.value === rolSeleccionado)?.idPerfil ||
        (usuario.idPerfil ? Number(usuario.idPerfil) : null),
      idUsuarioEstado: usuario.idUsuarioEstado || ESTADO_USUARIO_HABILITADO,
      razonSocial: razonSocialAdmin || ''
    }
  }

  const guardarCambios = async () => {
    if (seleccionados.length === 0) {
      setMensajeGuardado('No hay cambios seleccionados para guardar.')
      return
    }

    const empid = obtenerDelLocalStorage('empid')

    if (!empid) {
      setMensajeGuardado('No se encontró el usuario logueado.')
      return
    }

    const usuariosSeleccionados = usuarios.filter((usuario) =>
      seleccionados.includes(usuario.idRegistro)
    )

    const usuariosParaGuardar = usuariosSeleccionados.map((usuario) =>
      construirUsuarioPayload(empid, usuario)
    )

    try {
      setGuardando(true)
      setMensajeGuardado('')
      await usuariosEBPService.guardarUsuarios({
        usuarios: usuariosParaGuardar
      })

      setMensajeGuardado(
        `Se guardaron ${usuariosParaGuardar.length} colaborador(es) correctamente.`
      )
      setSeleccionados([])
      await cargarUsuarios()
    } catch {
      setMensajeGuardado('Ocurrió un error al guardar los colaboradores. Intente nuevamente.')
    } finally {
      setGuardando(false)
    }
  }

  const handleChangeModal = ({ target }) => {
    const { name, value } = target
    const valorNormalizado = normalizarValorCampo(name, value)

    if (name === 'idProvincia') {
      setLocalidades([])
      const camposActualizados =
        modalActivoId === 'administracion-usuarios-editar'
          ? buildCamposModalBase(
              provinciasOptions,
              [],
              ROLES_DISPONIBLES,
              Boolean(valorNormalizado)
            )
          : buildCamposModalAlta(
              provinciasOptions,
              [],
              ROLES_DISPONIBLES,
              Boolean(valorNormalizado)
            )

      dispatch(
        setModal({
          values: {
            idProvincia: valorNormalizado,
            idLocalidad: ''
          },
          campos: camposActualizados,
          errores: {}
        })
      )

      if (valorNormalizado) {
        fetchLocalidadesAPI
          .buscarLocalidadesPorIdProvincia(valorNormalizado)
          .then(({ data }) => {
            setLocalidades(normalizarRespuestaAPI(data))
          })
          .catch(() => {
            setLocalidades([])
          })
      }

      return
    }

    if (name === 'idLocalidad') {
      dispatch(
        setModal({
          values: { [name]: valorNormalizado },
          errores: {}
        })
      )

      return
    }

    dispatch(
      setModal({
        values: { [name]: valorNormalizado },
        errores: {}
      })
    )
  }

  const handleCloseModal = () => {
    setUsuarioEditandoId(null)
  }

  const actualizarUsuarioDesdeModal = ({
    idRegistro,
    nombre,
    apellido,
    email,
    telefono,
    dni,
    idLocalidad,
    rol,
    cuit
  }) => {
    setSeleccionados((prevState) =>
      prevState.includes(idRegistro) ? prevState : [...prevState, idRegistro]
    )

    setUsuarios((prevState) =>
      prevState.map((usuario) => {
        if (usuario.idRegistro !== idRegistro) return usuario

        const base = { ...usuario, nombre, apellido, email, telefono, dni, idLocalidad }

        if (typeof rol === 'string') {
          base.rol = rol
          base.idPerfil = ROLES_DISPONIBLES.find((r) => r.value === rol)?.idPerfil || ''
        }

        if (typeof cuit === 'string') {
          base.cuit = cuit
        }

        return base
      })
    )
  }

  const validarModalValues = (isAlta = false) => {
    const errores = {}
    const empid = obtenerDelLocalStorage('empid')

    if (isAlta) {
      if (!empid) {
        errores.general = 'No se encontró el usuario logueado.'
      }

      if (!modalValues.usuario?.trim()) {
        errores.usuario = 'El colaborador es obligatorio.'
      }
    }

    if (!modalValues.nombre?.trim()) {
      errores.nombre = 'El nombre es obligatorio.'
    }

    if (!modalValues.apellido?.trim()) {
      errores.apellido = 'El apellido es obligatorio.'
    }

    if (!modalValues.email?.trim()) {
      errores.email = 'El email es obligatorio.'
    }

    if (!modalValues.telefono?.trim()) {
      errores.telefono = 'El telefono es obligatorio.'
    } else if (!/^\d{1,10}$/.test(modalValues.telefono.trim())) {
      errores.telefono = 'El telefono debe contener solo números (máximo 10).'
    }

    if (!modalValues.rol?.trim()) {
      errores.rol = 'El rol es obligatorio.'
    }

    if (!modalValues.idProvincia) {
      errores.idProvincia = 'La provincia es obligatoria.'
    }

    if (!modalValues.idLocalidad) {
      errores.idLocalidad = 'La localidad es obligatoria.'
    }

    if (isAlta && !modalValues.cuit?.trim()) {
      errores.cuit = 'El CUIT es obligatorio.'
    } else if (modalValues.cuit.trim().length !== LONGITUD_CUIT) {
      errores.cuit = `El CUIT debe tener exactamente ${LONGITUD_CUIT} caracteres.`
    }

    if ((modalValues.email || '').trim().length > LONGITUD_EMAIL) {
      errores.email = `El email no puede superar ${LONGITUD_EMAIL} caracteres.`
    }

    if ((modalValues.nombre || '').trim().length > LONGITUD_NOMBRE_APELLIDO) {
      errores.nombre = `El nombre no puede superar ${LONGITUD_NOMBRE_APELLIDO} caracteres.`
    }

    if ((modalValues.apellido || '').trim().length > LONGITUD_NOMBRE_APELLIDO) {
      errores.apellido = `El apellido no puede superar ${LONGITUD_NOMBRE_APELLIDO} caracteres.`
    }

    const dniNormalizado = modalValues.dni?.toString().trim() || ''
    if (!dniNormalizado) {
      errores.dni = 'El DNI es obligatorio.'
    } else if (!/^\d{8}$/.test(dniNormalizado)) {
      errores.dni = 'El DNI debe contener exactamente 8 números.'
    }

    return { errores, dniNormalizado }
  }

  const createHandleConfirm = (isAlta) => async () => {
    const { errores, dniNormalizado } = validarModalValues(isAlta)

    if (Object.keys(errores).length > 0) {
      dispatch(setModal({ errores }))
      return false
    }

    const datosActualizar = {
      idRegistro: isAlta ? modalValues.idRegistro : usuarioEditandoId,
      nombre: modalValues.nombre.trim(),
      apellido: modalValues.apellido.trim(),
      email: modalValues.email.trim(),
      telefono: modalValues.telefono.trim(),
      dni: dniNormalizado,
      rol: modalValues.rol,
      idLocalidad: modalValues.idLocalidad,
      ...(isAlta && { cuit: modalValues.cuit?.toString().trim() || '' })
    }

    actualizarUsuarioDesdeModal(datosActualizar)

    if (!isAlta) {
      setUsuarioEditandoId(null)
    } else {
      setMensajeGuardado('Cambios listos. Presiona Guardar cambios para enviar todo junto.')
    }

    return true
  }

  const handleConfirmModal = createHandleConfirm(false)

  const handleConfirmAltaModal = createHandleConfirm(true)

  return (
    <section className="pageCard">
      <div className={styles.header}>
        <h2 className={styles.title}>Administración de colaboradores</h2>
        <div className={styles.headerActions}>
          {mensajeGuardado && <span className={styles.statusMessage}>{mensajeGuardado}</span>}
          <button
            type="button"
            className={styles.saveButton}
            onClick={guardarCambios}
            disabled={guardando}
          >
            {guardando ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </div>
      </div>

      <p className={styles.flowMessage}>
        Para dar de alta un colaborador, primero seleccionarlo con el checkbox y luego presionar
        Guardar cambios. Una vez dado de alta en eBuyMarket, podrá editarse.
      </p>

      <UsuariosFiltros fields={filtrosConfig} values={filtros} onChange={onChangeFiltro} />

      <div className={styles.tableContainer}>
        {cargando && <p className={styles.emptyState}>Cargando colaboradores...</p>}
        {!cargando && errorCarga && <p className={styles.emptyState}>{errorCarga}</p>}
        {!cargando && !errorCarga && (
          <table className={styles.table}>
            <thead>
              <tr className={styles.tableHeader}>
                <th className={styles.checkboxCell}>#</th>
                <th>Colaborador</th>
                <th>Nombre</th>
                <th>Apellido</th>
                <th>Email</th>
                <th>Teléfono</th>
                <th>DNI</th>
                <th>Rol</th>
                <th>Habilitado</th>
                <th className={styles.actionsCell}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {usuariosPaginados.map((usuario) => (
                <ItemUsuario
                  key={usuario.idRegistro}
                  usuario={usuario}
                  seleccionado={seleccionados.includes(usuario.idRegistro)}
                  onToggleSeleccion={toggleSeleccion}
                  puedeEditar={Boolean(usuario.habilitadoEnEBM)}
                  onEditar={abrirModalEdicion}
                  onAlta={abrirModalAlta}
                  onChangeEstado={actualizarEstadoUsuario}
                />
              ))}

              {usuariosFiltrados.length === 0 && (
                <tr>
                  <td colSpan="10" className={styles.emptyState}>
                    No hay colaboradores para los filtros seleccionados.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
        {!cargando && !errorCarga && usuariosFiltrados.length > 0 && (
          <Paginacion
            totalPaginas={totalPaginas}
            paginaActual={paginaActual}
            onCambioPagina={setPaginaActual}
            itemsPorPagina={itemsPorPagina}
            onCambioItemsPorPagina={(num) => {
              setItemsPorPagina(num)
              setPaginaActual(1)
            }}
          />
        )}
      </div>

      <GenericModal
        modalId={isAlta ? 'administracion-usuarios-alta' : 'administracion-usuarios-editar'}
        onClose={handleCloseModal}
        onChange={handleChangeModal}
        onConfirm={isAlta ? handleConfirmAltaModal : handleConfirmModal}
        ocultarGrid
      />
    </section>
  )
}

export default AdministracionUsuarios
